import React, { memo, useMemo } from 'react';
import { useGetDisbursement } from '../useGetDisbursement';
import { IDisbursementType } from './IDisbursementType';
import { Formik } from 'formik';
import { disbursementSchema } from '../NewDisbursementSchemas';
import { IDisbursementFormData } from 'types/order/escrow/IDisbursementFormData';
import { DisbursementRollup } from './DisbursementRollup';
import * as yup from 'yup';
import { useProcessDisbursement } from './useProcessDisbursement';
import { assertDefined } from 'utilities/Assertions';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { DisbursementToBeDeleted } from './DisbursementToBeDeleted';
import { DisbursementForm } from './DisbursementForm';
import { toast } from 'react-toastify';
import { ICreateSettlementFeeForm } from 'types/order/fees/ICreateSettlementItemForm';
import { eFeePropertyNames } from 'types/order/fees/eFeePropertyNames';
import { IOrderDetailsData } from '../../../../../ui-toolkit/orders/interfaces/IOrderDetailsData';
import { createFormikValidator } from 'utilities/validation/formikValidationHelper';

export interface IDisbursementContainerProps {
  disbursement: IDisbursementFormData;
  disbursements: IDisbursementFormData[];
  disbursementTypes: IDisbursementType[];
  fee: ICreateSettlementFeeForm;
  feeName: string;
  order: IOrderDetailsData;
  onRemove?: () => void;
  onPush: (value: IDisbursementFormData) => void;
}

export const DisbursementContainer: React.FC<IDisbursementContainerProps> = memo(
  ({ disbursementTypes, disbursement, disbursements, fee, feeName, order, onRemove, onPush }) => {
    const { data: itemData, isLoading } = useGetDisbursement({ disbursement });

    const initialValues = useMemo(() => ({ value: itemData }), [itemData]);

    const orderId = order.transaction.id;
    assertDefined(orderId);

    const processDisbursement = useProcessDisbursement(orderId);
    const onSubmit = async (values: { value: IDisbursementFormData }) => {
      try {
        const result = await processDisbursement(values.value);
        if (checkForMutationSuccess(result, 'saving disbursement')) {
          if (values.value.isNew) {
            onRemove?.();
          }
        }
      } catch (e: any) {
        toast('Failed to save', { type: 'error' });
      }
    };

    const validationSchema = useMemo(() => {
      return yup.object({
        value: disbursementSchema,
      });
    }, []);

    const validate = useMemo(() => {
      return createFormikValidator(validationSchema, { originalValue: itemData });
    }, [validationSchema, itemData]);

    return (
      <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize validate={validate}>
        {({ submitForm, dirty, values, isSubmitting, errors, submitCount }) => (
          <>
            <DisbursementRollup id={values.value.id} dirty={dirty || !!values.value.isNew} onSubmit={submitForm} />
            {!values.value.isDeleting && (
              <DisbursementForm
                fee={fee}
                feeName={feeName}
                disbursement={values.value}
                disbursements={disbursements}
                disbursementTypes={disbursementTypes}
                onRemove={onRemove}
                onPush={onPush}
                isDirty={dirty || !!values.value.isNew}
                isSubmitting={isSubmitting || isLoading}
                hasErrors={submitCount > 0 && !!errors.value}
              />
            )}
            {values.value.isDeleting && (
              <DisbursementToBeDeleted feeName={fee.stringFeePropertyValues[eFeePropertyNames.Name]} />
            )}
          </>
        )}
      </Formik>
    );
  }
);
